import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Search, FileText, Video, Download, File, Play, Clock, Eye, 
  BookOpen, CheckCircle, PlayCircle 
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

// Video interface
interface Video {
  id: string;
  title: string;
  description: string;
  video_url: string;
  thumbnail_url: string | null;
  transcript: string | null;
  duration: number | null;
  category: string;
  tags: string[];
  is_office_hours: boolean;
  recorded_date: string | null;
  view_count: number;
  created_at: string;
}

// Course interfaces
interface Course {
  id: string;
  title: string;
  slug: string;
  description: string;
  course_type: string;
  total_modules: number;
  estimated_duration: number;
  is_required: boolean;
  order_number: number;
}

interface UserProgress {
  course_id: string;
  completed_modules: number;
  total_modules: number;
  last_module_id?: string;
  last_module_number?: number;
}

export default function Resources() {
  const navigate = useNavigate();
  const { toast } = useToast();

  // Library state
  const [librarySearch, setLibrarySearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [stageFilter, setStageFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  // Videos state
  const [videos, setVideos] = useState<Video[]>([]);
  const [filteredVideos, setFilteredVideos] = useState<Video[]>([]);
  const [videoSearch, setVideoSearch] = useState("");
  const [videoCategory, setVideoCategory] = useState("all");

  // Courses state
  const [courses, setCourses] = useState<Course[]>([]);
  const [userProgress, setUserProgress] = useState<Record<string, UserProgress>>({});

  const [loading, setLoading] = useState(true);

  // Demo library data
  const allResources = [
    {
      id: 1,
      title: "Sample Constitution for Family Associations",
      type: "Sample",
      category: "Constitutional",
      stage: "Pre-Setup",
      description: "A ready to adapt sample you can use to draft your own Constitution.",
      downloads: 234,
    },
    {
      id: 2,
      title: "Understanding Your Constitution",
      type: "Video",
      category: "Constitutional",
      stage: "Pre-Setup",
      description: "Seth walks through the key parts of your Constitution.",
      views: 456,
    },
    {
      id: 3,
      title: "Sample Asset Transfer Agreement",
      type: "Sample",
      category: "Asset Transfer",
      stage: "Setup",
      description: "A sample agreement you can use to move assets into your Association.",
      downloads: 198,
    },
    {
      id: 4,
      title: "Banking Setup Guide",
      type: "Document",
      category: "Banking",
      stage: "Setup",
      description: "A step by step guide to setting up banking for your Association.",
      downloads: 145,
    },
    {
      id: 5,
      title: "The Heart of a Steward",
      type: "Video",
      category: "Constitutional",
      stage: "All",
      description: "Seth shares the biblical roots of stewardship.",
      views: 523,
    },
    {
      id: 6,
      title: "Sample Board Meeting Minutes",
      type: "Sample",
      category: "Governance",
      stage: "Operating",
      description: "A sample layout for writing your board meeting minutes (the written record of a meeting).",
      downloads: 87,
    },
  ];

  const filteredLibrary = allResources.filter((resource) => {
    const matchesSearch =
      librarySearch === "" ||
      resource.title.toLowerCase().includes(librarySearch.toLowerCase()) ||
      resource.description.toLowerCase().includes(librarySearch.toLowerCase());
    const matchesCategory =
      categoryFilter === "all" || resource.category === categoryFilter;
    const matchesStage =
      stageFilter === "all" || resource.stage === stageFilter || resource.stage === "All";
    const matchesType =
      typeFilter === "all" || resource.type === typeFilter;
    return matchesSearch && matchesCategory && matchesStage && matchesType;
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterVideos();
  }, [videoSearch, videoCategory, videos]);

  const fetchData = async () => {
    try {
      // Fetch videos
      const { data: videosData, error: videosError } = await supabase
        .from("videos")
        .select("*")
        .order("created_at", { ascending: false });

      if (videosError) throw videosError;
      setVideos(videosData || []);

      // Fetch courses
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: coursesData, error: coursesError } = await supabase
          .from("courses")
          .select("*")
          .order("order_number");

        if (coursesError) throw coursesError;
        setCourses(coursesData || []);

        // Fetch user progress
        const { data: progressData, error: progressError } = await supabase
          .from("user_course_progress")
          .select(`
            course_id,
            module_id,
            completed,
            course_modules!inner(order_number, course_id)
          `)
          .eq("user_id", user.id);

        if (progressError) throw progressError;

        // Calculate progress per course
        const progressMap: Record<string, UserProgress> = {};
        coursesData?.forEach(course => {
          const courseProgress = progressData?.filter(p => p.course_id === course.id) || [];
          const completedModules = courseProgress.filter(p => p.completed).length;
          const lastIncomplete = courseProgress.find(p => !p.completed);
          
          progressMap[course.id] = {
            course_id: course.id,
            completed_modules: completedModules,
            total_modules: course.total_modules,
            last_module_id: lastIncomplete?.module_id,
            last_module_number: lastIncomplete?.course_modules?.order_number
          };
        });

        setUserProgress(progressMap);
      }
    } catch (error: any) {
      toast({
        title: "Error loading resources",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const filterVideos = () => {
    let filtered = videos;

    if (videoCategory !== "all") {
      if (videoCategory === "office_hours") {
        filtered = filtered.filter((v) => v.is_office_hours);
      } else {
        filtered = filtered.filter((v) => v.category === videoCategory);
      }
    }

    if (videoSearch) {
      const query = videoSearch.toLowerCase();
      filtered = filtered.filter(
        (v) =>
          v.title.toLowerCase().includes(query) ||
          v.description?.toLowerCase().includes(query) ||
          v.transcript?.toLowerCase().includes(query) ||
          v.tags.some((tag) => tag.toLowerCase().includes(query))
      );
    }

    setFilteredVideos(filtered);
  };

  const handleVideoClick = async (video: Video) => {
    await supabase
      .from("videos")
      .update({ view_count: video.view_count + 1 })
      .eq("id", video.id);

    window.open(video.video_url, "_blank");
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return "Unknown";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const getCourseProgress = (courseId: string) => {
    const progress = userProgress[courseId];
    if (!progress) return 0;
    return Math.round((progress.completed_modules / progress.total_modules) * 100);
  };

  const getCourseStatus = (courseId: string) => {
    const progress = userProgress[courseId];
    if (!progress || progress.completed_modules === 0) return "not_started";
    if (progress.completed_modules === progress.total_modules) return "completed";
    return "in_progress";
  };

  const handleStartCourse = async (course: Course) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: modules } = await supabase
        .from("course_modules")
        .select("*")
        .eq("course_id", course.id)
        .order("order_number")
        .limit(1);

      if (modules && modules[0]) {
        navigate(`/learning/course/${course.slug}/module/${modules[0].order_number}`);
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleContinueCourse = (course: Course) => {
    const progress = userProgress[course.id];
    const moduleNumber = progress?.last_module_number || 1;
    navigate(`/learning/course/${course.slug}/module/${moduleNumber}`);
  };

  const videoCategories = ["all", "office_hours", ...Array.from(new Set(videos.map((v) => v.category)))];

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Resources</h1>
          <p className="text-muted-foreground">
            Sample documents, teaching videos, and courses to guide your stewardship journey.
          </p>
        </div>

        <Tabs defaultValue="templates" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 max-w-2xl">
            <TabsTrigger value="templates">Samples</TabsTrigger>
            <TabsTrigger value="videos">Videos</TabsTrigger>
            <TabsTrigger value="courses">Courses</TabsTrigger>
          </TabsList>

          {/* Samples Tab */}
          <TabsContent value="templates" className="space-y-6">
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search samples, documents, and resources..."
                  value={librarySearch}
                  onChange={(e) => setLibrarySearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <div className="flex flex-wrap gap-4">
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="Constitutional">Constitutional</SelectItem>
                    <SelectItem value="Asset Transfer">Asset Transfer</SelectItem>
                    <SelectItem value="Banking">Banking</SelectItem>
                    <SelectItem value="Governance">Governance</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={stageFilter} onValueChange={setStageFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Stage" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Stages</SelectItem>
                    <SelectItem value="Pre-Setup">Pre-Setup</SelectItem>
                    <SelectItem value="Setup">Setup Phase</SelectItem>
                    <SelectItem value="Operating">Operating</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="Document">Documents</SelectItem>
                    <SelectItem value="Video">Videos</SelectItem>
                    <SelectItem value="Sample">Sample documents</SelectItem>
                  </SelectContent>
                </Select>

                {(categoryFilter !== "all" || stageFilter !== "all" || typeFilter !== "all") && (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setCategoryFilter("all");
                      setStageFilter("all");
                      setTypeFilter("all");
                    }}
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            </div>

            <p className="text-sm text-muted-foreground">
              {filteredLibrary.length} {filteredLibrary.length === 1 ? "resource" : "resources"} found
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredLibrary.map((resource) => (
                <Card 
                  key={resource.id} 
                  className="hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => {
                    if (resource.type === "Document" || resource.type === "Sample") {
                      navigate(`/resources/samples/${resource.id}`);
                    }
                  }}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {resource.type === "Video" ? (
                          <Video className="h-5 w-5 text-primary" />
                        ) : resource.type === "Sample" ? (
                          <File className="h-5 w-5 text-primary" />
                        ) : (
                          <FileText className="h-5 w-5 text-primary" />
                        )}
                        <span className="text-xs font-medium text-muted-foreground">
                          {resource.type === "Sample" ? "Sample document" : resource.type}
                        </span>
                      </div>
                      <Button variant="ghost" size="icon">
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                    <CardTitle className="text-lg">{resource.title}</CardTitle>
                    <CardDescription className="line-clamp-2">
                      {resource.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className="text-xs bg-secondary text-secondary-foreground px-2 py-1 rounded">
                        {resource.category}
                      </span>
                      <span className="text-xs bg-accent text-accent-foreground px-2 py-1 rounded">
                        {resource.stage}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {resource.downloads !== undefined
                        ? `${resource.downloads} downloads`
                        : `${resource.views} views`}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Videos Tab */}
          <TabsContent value="videos" className="space-y-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search videos, transcripts, or tags..."
                  value={videoSearch}
                  onChange={(e) => setVideoSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={videoCategory} onValueChange={setVideoCategory}>
                <SelectTrigger className="w-full md:w-[200px]">
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Videos</SelectItem>
                  <SelectItem value="office_hours">Office Hours</SelectItem>
                  {videoCategories
                    .filter((c) => c !== "all" && c !== "office_hours")
                    .map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {filteredVideos.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Video className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No videos found</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredVideos.map((video) => (
                  <Card key={video.id} className="hover:shadow-lg transition-shadow cursor-pointer">
                    <CardHeader>
                      <div className="flex items-start justify-between gap-2">
                        <CardTitle className="text-lg line-clamp-2">{video.title}</CardTitle>
                        {video.is_office_hours && (
                          <Badge variant="secondary" className="shrink-0">
                            Office Hours
                          </Badge>
                        )}
                      </div>
                      <CardDescription className="line-clamp-2">{video.description}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex flex-wrap gap-2">
                        {video.tags.slice(0, 3).map((tag) => (
                          <Badge key={tag} variant="outline">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                      <div className="flex items-center justify-between text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {formatDuration(video.duration)}
                        </div>
                        <div className="flex items-center gap-1">
                          <Eye className="h-4 w-4" />
                          {video.view_count} views
                        </div>
                      </div>
                      <Button onClick={() => handleVideoClick(video)} className="w-full" size="sm">
                        <Play className="h-4 w-4 mr-2" />
                        Watch Video
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Courses Tab */}
          <TabsContent value="courses" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {courses.map((course) => {
                const status = getCourseStatus(course.id);
                const progressPercent = getCourseProgress(course.id);

                return (
                  <Card key={course.id} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex gap-2">
                          {course.is_required && (
                            <Badge variant="default">Required</Badge>
                          )}
                          <Badge variant="outline" className="capitalize">
                            {course.course_type}
                          </Badge>
                        </div>
                        {status === "completed" && (
                          <CheckCircle className="w-5 h-5 text-accent" />
                        )}
                        {status === "in_progress" && (
                          <PlayCircle className="w-5 h-5 text-primary" />
                        )}
                      </div>
                      <CardTitle className="text-xl">{course.title}</CardTitle>
                      <CardDescription>{course.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between text-sm text-muted-foreground">
                          <span>{course.total_modules} modules</span>
                        </div>
                        
                        {status !== "not_started" && (
                          <div>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="font-medium">{progressPercent}% Complete</span>
                              <span className="text-muted-foreground">
                                {userProgress[course.id]?.completed_modules} of {course.total_modules}
                              </span>
                            </div>
                            <Progress value={progressPercent} className="h-2" />
                          </div>
                        )}

                        <Button 
                          className="w-full"
                          onClick={() => status === "not_started" ? handleStartCourse(course) : handleContinueCourse(course)}
                        >
                          {status === "not_started" && "Start Course"}
                          {status === "in_progress" && `Continue Module ${userProgress[course.id]?.last_module_number}`}
                          {status === "completed" && "Review Course"}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {courses.length === 0 && (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <BookOpen className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No courses available yet</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
}
